import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import CollapseElem from 'Components/Collapse';
import HistoryRow from 'Components/HistoryRow';
import HistoryRowControls from 'Components/HistoryRowControls';
import HistoryRowSubHeader from 'Components/HistoryRowSubHeader';
import HistoryRowVerticalItem from 'Components/HistoryRowVerticalItem';
import HistorySection from 'Components/HistorySection';
import DocumentCreationButton from 'Components/DocumentCreationButton';
import { EditingLockedContentContext } from 'Containers/EditingLockedContentContext';
import { formatPartialDate } from 'Utility/date';
import { fm } from 'Components/FormatMessage';
import { sortDocumentsDescending } from 'Utility/documentHandling';

const StyledSubHeader = ({ title }: IStyledRow): React.JSX.Element => <HistoryRowSubHeader header={title} />;

const StyledRow = ({ title, value, condition, id }: IStyledRow): React.JSX.Element => (
  <React.Fragment>
    <span id={id} />
    {condition === true ? <HistoryRowVerticalItem header={title} value={value ?? '-'} /> : undefined}
  </React.Fragment>
);

const TTDataSection = ({ document }: ITTDataSection): React.JSX.Element => {
  return (
    <React.Fragment>
      {document.atrophy || document.otherClinicallyRelevantFindingFound ? (
        <React.Fragment>
          {document.atrophy ? (
            <React.Fragment>
              <StyledSubHeader title={fm('imaging.atrophy')} />
              <StyledRow
                id={'atrophyTTHistory'}
                title={fm('imaging.atrophy')}
                value={fm(`imaging.opts.atrophy.${document.atrophy}`)}
                condition={true}
              />
              <StyledRow
                title={fm('imaging.atrophyLocalization')}
                value={document.atrophyLocalization}
                condition={document.atrophy === 'focal'}
              />
            </React.Fragment>
          ) : null}
          {document.otherClinicallyRelevantFindingFound && (
            <React.Fragment>
              <StyledSubHeader title={fm('imaging.other')} />
              <StyledRow
                id={'otherRelevantTTHistory'}
                title={fm('imaging.otherClinicallyRelevantFindingFound')}
                value={fm(`imaging.opts.yesNo.${document.otherClinicallyRelevantFindingFound}`)}
                condition={true}
              />
              <StyledRow
                title={fm('imaging.whatFinding')}
                value={document.otherClinicallyRelevantFinding}
                condition={document.otherClinicallyRelevantFindingFound === 'yes'}
              />
            </React.Fragment>
          )}
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};

const TT = ({ documents, startEdit }: IOwnProps): React.JSX.Element => {
  const showProcedureCode = (d: ITT) => d.procedureCode && d.procedureCode.length > 0;
  const showStatement = (d: ITT) => d.statement && d.statement.length > 0;
  const { openEditing } = React.useContext(EditingLockedContentContext);

  return (
    <HistorySection
      headerText={<FormattedMessage id={'imaging.tt'} />}
      newButton={
        <DocumentCreationButton
          name="tt"
          text={'imaging.newtt'}
          onClick={startEdit({}, 'tt')}
          width={15}
          height={3}
          fontSize={14}
          alternate={true}
        />
      }
      hasHistoryRows={true}
    >
      {documents.toSorted(sortDocumentsDescending).map((d, index) => (
        <React.Fragment key={d._id}>
          <HistoryRow
            headerText={formatPartialDate(d.date)}
            rowButton={
              <HistoryRowControls
                document={d}
                startEdit={
                  d._lockedFor && d._source !== null ? doc => () => openEditing(doc, 'imaging.scoliosis') : startEdit
                }
              />
            }
            controlsMargin={false}
          >
            <>
              {showProcedureCode(d) && (
                <StyledRow
                  id={'procedureCode'}
                  title={fm('imaging.procedureCode')}
                  value={d.procedureCode}
                  condition={true}
                />
              )}
              {showStatement(d) && (
                <StyledRow id={'statement'} title={fm('imaging.statementText')} value={d.statement} condition={true} />
              )}
              {index === 0 ? (
                <TTDataSection document={d} />
              ) : d.atrophy || d.otherClinicallyRelevantFindingFound ? (
                <CollapseElem
                  localeIDs={{ showMessage: 'imaging.show', hideMessage: 'imaging.hide' }}
                  amount={documents.length}
                >
                  <div style={{ marginBottom: '1rem' }}>
                    <TTDataSection document={d} />
                  </div>
                </CollapseElem>
              ) : null}
            </>
          </HistoryRow>
        </React.Fragment>
      ))}
    </HistorySection>
  );
};

interface ITTDataSection {
  document: ITT;
}

interface IStyledRow {
  title: string | React.JSX.Element;
  value?: any;
  condition?: boolean;
  id?: string;
}

interface IOwnProps {
  documents: IDocumentWithTypeV2<'tt'>[];
  startEdit: StartEdit;
}

export default TT;
